import axios from 'axios';
import applyInterceptors from '../interceptors';
import { API_KEYS } from '../common/consts';
import { RequestError } from '../common/errors';
import Idel2ResultsClientWithKeycloak from './Idel2ResultsClientWithKeycloak';
import Idel2ContentClient from './Idel2ContentClient';
import Idel2ResultsClient from './Idel2ResultsClient';
import Idel2TranslatorClient from './Idel2TranslatorClient';
import Idel2ZoGClient from './Idel2ZoGClient';
import Idel2StaticContentClient from './Idel2StaticContentClient';
import Idel2ApplicationContentClient from './Idel2ApplicationContentClient';
import CLPLContentClient from '../clpl/CLPLContentClient';

const axiosInstance = axios.create();
applyInterceptors(axiosInstance);

const idel2ContentClient = new Idel2ContentClient(axiosInstance);
const idel2ResultsClient = new Idel2ResultsClient(axiosInstance);
const idel2TranslatorClient = new Idel2TranslatorClient(axiosInstance);
const idel2ZoGClient = new Idel2ZoGClient(axiosInstance);
const idel2StaticContentClient = new Idel2StaticContentClient(axiosInstance);
const idel2ApplicationContentClient = new Idel2ApplicationContentClient(axiosInstance);
const idel2ResultsClientWithKeycloak = new Idel2ResultsClientWithKeycloak(axiosInstance);
const clplContentClient = new CLPLContentClient(axiosInstance);

const assessment = 'IDEL2';

/**
 * @param {(String)[]}  studentUids      - Array of students uids
 * @param {String}      [schoolYear]     - School year, optional parameter.
 * @param {String=}     classId          - Class id, optional parameter.
 *
 * @param {String}      instId If set, this field will enable support of custom gating rules
 * @param {String}      token            - keycloak token for API authorization
 * @param {String}      staffId          - staffId for an additional header used in results caching.
 * @return {Promise<{results: Object[], studentStatuses: Object[]}>}      - Results from Results API
 */
export const getIdel2ResultsAndStatuses = async (
    studentUids,
    schoolYear,
    classId,
    instId,
    token,
    staffId = '',
) => {
    try {
        return await idel2TranslatorClient.getResults(
            studentUids,
            schoolYear,
            classId,
            true,
            true,
            true,
            token,
            staffId,
        );
    } catch (error) {
        throw new RequestError(`${assessment} get results request failed`, error, assessment);
    }
};

/**
 * @param {(String)[]}  studentUids      - Array of students uids
 * @param {String}      [schoolYear]     - School year, optional parameter.
 * @param {Boolean}     includeInvalidated - Whether to include invalidated results
 * @param {String=}     classId          - Class id, optional parameter.
 *
 * @return {Promise<{results: Object[]}>}      - Results from Results API
 */
export const getIdel2Results = async (studentUids, schoolYear, includeInvalidated, classId) => {
    try {
        const results = await idel2ResultsClient.getResults(
            studentUids,
            schoolYear,
            includeInvalidated,
            classId,
        );

        return {
            [API_KEYS.RESULTS]: results,
        };
    } catch (error) {
        throw new RequestError(`${assessment} get results request failed`, error, assessment);
    }
};

/**
 * Passes through given results array and decide whether the result should be created or updated
 */
export const persistIdel2Results = async results => {
    try {
        const response = await idel2ResultsClient.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

export const persistIdel2ResultsWithKeycloak = async (results, token) => {
    try {
        idel2ResultsClientWithKeycloak.setToken(token);
        const response = await idel2ResultsClientWithKeycloak.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

/**
 * @return {{ forms: Object[], cutscores: Object[], reminders: Object[], instructions: Object[] }} Idel2 content
 * (forms, cutscores, reminders, instructions)
 */
export const getIdel2Content = async () => {
    try {
        const [
            forms,
            cutscores,
            reminders,
            instructions,
            clplCutscores,
            clplForms,
        ] = await Promise.all([
            idel2ContentClient.getForms(),
            idel2ContentClient.getCutscores(),
            idel2StaticContentClient.getReminders(),
            idel2StaticContentClient.getInstructions(),
            clplContentClient.getCutscores(), // we need to get clpl cutscores for idel2 in order to work with recommendations
            clplContentClient.getForms(), // clpl forms for lectura recommendations
        ]);

        return {
            [API_KEYS.FORMS]: forms,
            [API_KEYS.CUTSCORES]: cutscores,
            [API_KEYS.REMINDERS]: reminders,
            [API_KEYS.INSTRUCTIONS]: instructions,
            [API_KEYS.CLPL_CUTSCORES]: clplCutscores,
            [API_KEYS.CLPL_FORMS]: clplForms,
        };
    } catch (error) {
        throw new RequestError(`${assessment} get content request failed`, error, assessment);
    }
};

/**
 * @param {(String)[]}  studentUids    - Array of students uids
 * @param {String}      schoolYear     - School year, optional parameter. (E.g. for 18/19 it is 2019)
 *
 * @param token keycloak token
 * @return {Promise<{ Object[]} } list of student goals or empty list
 */
export const getIdel2StudentGoals = async (studentUids, schoolYear, token) => {
    try {
        return await idel2ZoGClient.getStudentGoals(studentUids, schoolYear, token);
    } catch (error) {
        throw new RequestError(`${assessment} get student goals request failed`, error, assessment);
    }
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Idel2 reminders
 */
export const getIdel2Reminders = async token => {
    let reminders;
    try {
        reminders = await idel2StaticContentClient.getReminders(token);
    } catch (error) {
        throw new RequestError(`${assessment} get reminders request failed`, error, assessment);
    }

    return reminders || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Idel2 instructions
 */
export const getIdel2Instructions = async token => {
    let instructions;
    try {
        instructions = await idel2StaticContentClient.getInstructions(token);
    } catch (error) {
        throw new RequestError(`${assessment} get instructions request failed`, error, assessment);
    }

    return instructions || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Idel2 forms
 */
export const getIdel2Forms = async token => {
    let forms;
    try {
        forms = await idel2ContentClient.getForms([], token);
    } catch (error) {
        throw new RequestError(`${assessment} get forms request failed`, error, assessment);
    }

    return forms || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Idel2 cutscores
 */
export const getIdel2Cutscores = async token => {
    let cutscores;
    try {
        cutscores = await idel2ContentClient.getCutscores([], token);
    } catch (error) {
        throw new RequestError(`${assessment} get cutscores request failed`, error, assessment);
    }

    return cutscores || [];
};

export {
    Idel2ContentClient,
    Idel2ResultsClient,
    Idel2StaticContentClient,
    Idel2TranslatorClient,
    Idel2ZoGClient,
    Idel2ResultsClientWithKeycloak,
    Idel2ApplicationContentClient,
    idel2ApplicationContentClient,
    applyInterceptors,
    RequestError,
    API_KEYS,
};
